import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert } from '@mui/material';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';

/**
 * Log Explorer - Platform Admin
 * 
 * PLATFORM-LEVEL FEATURE:
 * - Access to ALL tenant logs + infrastructure logs
 * - No tenant_id filtering (unrestricted access)
 * - For debugging, compliance, and platform monitoring
 * 
 * SECURITY RATIONALE:
 * - Tenant access removed to prevent data exfiltration (€15M GDPR fine risk)
 * - Prevents SQL injection via complex queries
 * - Eliminates performance impact from expensive queries
 * - Platform team only (compliance requirement)
 * 
 * MIGRATED FROM: /src/features/advanced/components/logs/LogExplorer.tsx
 * CHANGES: Added platform-admin warning, removed tenant_id filtering, added audit logging
 */

interface LogEntry {
  timestamp: string;
  level: string;
  source: string;
  message: string;
  tenant_id?: string;
}

const TIME_RANGE_TO_HOURS: Record<string, number> = {
  '15m': 1,
  '1h': 1,
  '24h': 24,
  '7d': 168,
  '30d': 168 // Backend max is 168h
};

export const LogExplorer: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [source, setSource] = useState('');
  const [severity, setSeverity] = useState('');
  const [eventType, setEventType] = useState('');
  const [timeRange, setTimeRange] = useState('24h');
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [accessReason, setAccessReason] = useState('');
  const [auditError, setAuditError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logResults, setLogResults] = useState<LogEntry[]>([]);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearchClick = () => {
    // Open reason dialog before allowing search
    setReasonDialogOpen(true);
  };

  const handleConfirmSearch = async () => {
    if (!accessReason.trim()) {
      return; // Reason is required
    }

    setIsSubmitting(true);
    setAuditError(null);

    try {
      // CRITICAL: Send audit log to backend FIRST
      // Log access cannot proceed without audit trail
      const token = localStorage.getItem('astrapeak_platform_admin_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const auditResponse = await fetch(`${getBaseUrl()}/api/platform-admin/audit/log-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'log_search',
          reason: accessReason,
          search_params: {
            query: searchQuery,
            source,
            severity,
            eventType,
            timeRange
          }
        })
      });

      if (!auditResponse.ok) {
        const errorData = await auditResponse.json().catch(() => ({ detail: 'Audit logging failed' }));
        const detail = errorData.detail;
        throw new Error(typeof detail === 'string' ? detail : 'Failed to record audit entry');
      }

      // ONLY after successful audit logging, proceed with search
      setReasonDialogOpen(false);
      setAccessReason('');
      setAuditError(null);

      // Fetch logs from platform logs endpoint
      setIsSearching(true);
      setSearchError(null);
      try {
        const hours = TIME_RANGE_TO_HOURS[timeRange] || 24;
        const params = new URLSearchParams({ limit: '100', hours: String(hours) });
        if (severity) params.append('level', severity.toUpperCase());
        if (source) params.append('source', source);

        const logsResponse = await fetch(`${getBaseUrl()}/api/platform/logs?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!logsResponse.ok) {
          throw new Error('Failed to fetch platform logs');
        }

        const logsData = await logsResponse.json();
        let logs: LogEntry[] = Array.isArray(logsData.logs) ? logsData.logs : [];

        // Client-side text filter if searchQuery provided
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          logs = logs.filter(log =>
            log.message?.toLowerCase().includes(query) ||
            log.source?.toLowerCase().includes(query)
          );
        }

        setLogResults(logs);
        setSearchPerformed(true);
      } catch (searchErr) {
        setSearchError(searchErr instanceof Error ? searchErr.message : 'Search failed');
      } finally {
        setIsSearching(false);
      }
    } catch (error) {
      // CRITICAL: If audit fails, DO NOT proceed with search
      const errorMessage = error instanceof Error ? error.message : 'Failed to record audit entry';
      setAuditError(errorMessage);
      console.error('Audit logging failed:', error);
      // Dialog stays open, user must retry or cancel
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSearch = () => {
    setReasonDialogOpen(false);
    setAccessReason('');
  };

  return (
    <div className="px-4 py-6">
      {/* Platform Admin Warning Banner */}
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-red-500 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <h3 className="text-red-500 font-medium mb-1">Unrestricted Access - Handle with Care</h3>
            <p className="text-red-200/80 text-sm">
              This interface provides access to logs from ALL tenants and infrastructure components. 
              Ensure compliance with data protection regulations (GDPR, HIPAA, SOC2). All searches are logged.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white">Log Explorer</h2>
        <p className="text-sm text-gray-400">Search and analyze security logs across ALL tenants and infrastructure</p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search logs across all tenants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
          <button 
            onClick={handleSearchClick}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Search
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <select 
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-white"
        >
          <option value="">All Sources</option>
          <option value="api">API</option>
          <option value="ml">ML Pipeline</option>
          <option value="connector">Connectors</option>
          <option value="auth">Authentication</option>
          <option value="scheduler">Scheduler</option>
        </select>
        <select 
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          className="bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-white"
        >
          <option value="">All Levels</option>
          <option value="CRITICAL">Critical</option>
          <option value="ERROR">Error</option>
          <option value="WARNING">Warning</option>
          <option value="INFO">Info</option>
          <option value="DEBUG">Debug</option>
        </select>
        <select 
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          className="bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-white"
          disabled
        >
          <option value="">All Events</option>
        </select>
        <select 
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-white"
        >
          <option value="15m">Last 15 minutes</option>
          <option value="1h">Last hour</option>
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
      </div>

      {/* Loading State */}
      {isSearching && (
        <div className="flex justify-center items-center p-8">
          <div className="text-gray-400">Searching platform logs...</div>
        </div>
      )}

      {/* Search Error */}
      {searchError && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6">
          <p className="text-red-400">{searchError}</p>
        </div>
      )}

      {/* Log Table */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead>
              <tr className="bg-gray-900/50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Message
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {!searchPerformed && !isSearching && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                    Enter search criteria and click Search to query platform logs. All searches require audit justification.
                  </td>
                </tr>
              )}
              {searchPerformed && logResults.length === 0 && !isSearching && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                    No logs found matching the current filters.
                  </td>
                </tr>
              )}
              {logResults.map((log, index) => (
                <tr key={index} className="hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {log.timestamp || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      log.level === 'ERROR' || log.level === 'CRITICAL' ? 'bg-red-900/50 text-red-400' :
                      log.level === 'WARNING' ? 'bg-yellow-900/50 text-yellow-400' :
                      'bg-blue-900/50 text-blue-400'
                    }`}>
                      {log.level || 'INFO'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-900/50 text-purple-400">
                      {log.source || 'unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300 max-w-md truncate">
                    {log.message || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Results Count */}
      {searchPerformed && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Showing {logResults.length} results (across all tenants)
          </div>
        </div>
      )}

      {/* Compliance Notice */}
      <div className="mt-6 bg-gray-800/50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Compliance & Security</h3>
        <ul className="text-sm text-gray-400 space-y-1">
          <li>• All log searches are audited and logged</li>
          <li>• Access restricted to platform administrators only</li>
          <li>• Data retention follows GDPR/HIPAA/SOC2 requirements</li>
          <li>• PII data is masked by default</li>
          <li>• Export functionality requires additional approval</li>
        </ul>
      </div>

      {/* Reason for Access Dialog */}
      <Dialog open={reasonDialogOpen} onClose={isSubmitting ? undefined : handleCancelSearch} maxWidth="sm" fullWidth>
        <DialogTitle>Reason for Log Access</DialogTitle>
        <DialogContent>
          <p className="text-gray-600 text-sm mb-4">
            You are about to access logs from all tenants. Please provide a reason for this access.
            This will be recorded in the audit trail for compliance purposes.
          </p>
          {auditError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Audit Logging Failed:</strong> {auditError}
              <br />
              <span className="text-sm">Log access cannot proceed without audit trail. Please retry or contact support.</span>
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for accessing logs"
            value={accessReason}
            onChange={(e) => setAccessReason(e.target.value)}
            placeholder="e.g., Investigating security incident #12345, debugging production issue, compliance audit"
            autoFocus
            required
            disabled={isSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSearch} disabled={isSubmitting}>Cancel</Button>
          <Button
            onClick={handleConfirmSearch}
            variant="contained"
            color="primary"
            disabled={!accessReason.trim() || accessReason.trim().length < 10 || isSubmitting}
          >
            {isSubmitting ? 'Recording Audit...' : 'Confirm and Search'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LogExplorer;
